import { useState } from 'react';

import { ChoiceList } from '@/modules/signup-flow/components/layout/ChoiceList';
import { buttonClass } from '@/modules/signup-flow/components/layout/motion';
import {
  Field,
  StepActions,
} from '@/modules/signup-flow/components/layout/SignupLayoutParts';
import { StepHeader } from '@/modules/signup-flow/components/layout/StepHeader';
import {
  SIGN_UP_FLOW_STEPS,
  signupFlowStep,
} from '@/modules/signup-flow/signupFlowSteps';
import { isValidWebsiteUrl } from '@/modules/signup-flow/websiteUrl';
import { Button } from '@/modules/ui/components/button/Button';
import { Input } from '@/modules/ui/components/input/Input';

export interface CompanyProfile {
  name: string;
  websiteUrl: string;
  size: string;
  location: string;
}

const companySizes = [
  { value: '1-10', label: '1 to 10 people' },
  { value: '10-50', label: '10 to 50 people' },
  { value: '50-100', label: '50 to 100 people' },
  { value: '100+', label: 'More than 100 people' },
];

export const DEFAULT_COMPANY_SIZE = '1-10';

interface CompanyProfileFormProps {
  firstName: string;
  /** Read once on mount, like a form's default values. */
  initial: CompanyProfile;
  isSubmitting?: boolean;
  onContinue: (profile: CompanyProfile) => void;
}

/** Step 1, presentational; `AboutCompany.tsx` wires data and persistence. */
export const CompanyProfileForm = ({
  firstName,
  initial,
  isSubmitting = false,
  onContinue,
}: CompanyProfileFormProps) => {
  const [profile, setProfile] = useState(initial);
  const [submitted, setSubmitted] = useState(false);

  const nameError =
    submitted && !profile.name.trim() ? 'Add your company name.' : undefined;
  const websiteError = !submitted
    ? undefined
    : !profile.websiteUrl.trim()
      ? 'Add your company website.'
      : !isValidWebsiteUrl(profile.websiteUrl)
        ? 'Enter the full address, like https://northstar.design.'
        : undefined;

  const isValid =
    Boolean(profile.name.trim()) && isValidWebsiteUrl(profile.websiteUrl);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (isValid)
      onContinue({ ...profile, websiteUrl: profile.websiteUrl.trim() });
  };

  return (
    <form className="flex flex-col gap-8" onSubmit={submit} noValidate>
      <StepHeader
        step={signupFlowStep.aboutCompany}
        totalSteps={SIGN_UP_FLOW_STEPS}
        title={`Hi ${firstName}, tell us about your company.`}
        subtitle="Finlay uses this when it introduces you to candidates."
      />

      <div className="flex flex-col gap-5">
        <Field
          label="Company name"
          htmlFor="company-name"
          required
          error={nameError}
        >
          <Input
            id="company-name"
            autoFocus
            aria-required
            value={profile.name}
            placeholder="Northstar Design"
            aria-invalid={Boolean(nameError)}
            onChange={(event) =>
              setProfile({ ...profile, name: event.target.value })
            }
          />
        </Field>
        <Field
          label="Website"
          htmlFor="company-website"
          required
          error={websiteError}
        >
          <Input
            id="company-website"
            type="url"
            aria-required
            value={profile.websiteUrl}
            placeholder="https://northstar.design"
            aria-invalid={Boolean(websiteError)}
            onChange={(event) =>
              setProfile({ ...profile, websiteUrl: event.target.value })
            }
          />
        </Field>
        <Field label="Company size" required>
          <ChoiceList
            name="Company size"
            required
            value={profile.size}
            options={companySizes}
            onChange={(size) => setProfile({ ...profile, size })}
          />
        </Field>
        <Field label="Headquarters" htmlFor="company-location" hint="Optional">
          <Input
            id="company-location"
            value={profile.location}
            placeholder="Prague, Czechia"
            onChange={(event) =>
              setProfile({ ...profile, location: event.target.value })
            }
          />
        </Field>
      </div>

      <StepActions>
        <Button type="submit" className={buttonClass} loading={isSubmitting}>
          Continue
        </Button>
      </StepActions>
    </form>
  );
};
